import { useResource } from '@landsbankinn/l16n';
import React from 'react';
import type { fetchFunds_funds_nodes } from '../../../../graphql/__generated__/fetchFunds';
import { DataTable } from '../../../table/DataTable';
import { FundDataType } from '../Funds';
import type {
  AnnualizedReturnOptions,
  NominalReturnOptions,
} from '../returns-selector-tabs/useReturnsConfig';
import { FundsTableContainer } from './FundsTableContainer';
import { useAnnualizedData } from './useAnnualizedData';
import { useNominalData } from './useNominalData';
import { usePriceData } from './usePriceData';

interface IProps {
  data: fetchFunds_funds_nodes[];
  fundDataType: FundDataType;
  selectedNominalReturn: NominalReturnOptions;
  selectedAnnualizedReturn: AnnualizedReturnOptions;
  category: string;
  fundDetailPrefix: string;
  fundBuyUrl: (symbol: string) => string;
  stockBuyUrl: (symbol: string) => string;
}

enum FundCategories {
  'BondFund' = 'Skuldabréfasjóðir',
  'EquityFund' = 'Hlutabréfasjóðir',
  'BalancedFund' = 'Blandaðir sjóðir',
  'ExchangeTradedFund' = 'Kauphallarsjóðir',
  'PrivateBankingFund' = 'Sjóðir Einkabankaþjónustu',
  'UknownClassification' = 'Óþekktur flokkur',
}

enum FundCategoriesEN {
  'BondFund' = 'Bond funds',
  'EquityFund' = 'Equity funds',
  'BalancedFund' = 'Balanced funds',
  'ExchangeTradedFund' = 'ETFs',
  'PrivateBankingFund' = 'Private banking funds',
  'UknownClassification' = 'Unknown classification',
}

export function FundTable({
  data,
  fundDataType,
  selectedNominalReturn,
  selectedAnnualizedReturn,
  category,
  fundDetailPrefix,
  fundBuyUrl,
  stockBuyUrl,
}: IProps) {
  const { locale } = useResource();

  const nominalReturnColumns = useNominalData(
    data,
    locale === 'is' ? FundCategories[category] : FundCategoriesEN[category],
    selectedNominalReturn,
    fundBuyUrl,
    stockBuyUrl,
  );
  const annualizedColumns = useAnnualizedData(
    data,
    locale === 'is' ? FundCategories[category] : FundCategoriesEN[category],
    selectedAnnualizedReturn,
    fundBuyUrl,
    stockBuyUrl,
  );
  const priceColumns = usePriceData(
    data,
    locale === 'is' ? FundCategories[category] : FundCategoriesEN[category],
    fundBuyUrl,
    stockBuyUrl,
  );

  return (
    <FundsTableContainer fundDataType={fundDataType}>
      <DataTable<fetchFunds_funds_nodes>
        aria-label={
          locale === 'is'
            ? FundCategories[category]
            : FundCategoriesEN[category]
        }
        data={data}
        columns={
          fundDataType === FundDataType.NominalReturn
            ? (nominalReturnColumns as any)
            : fundDataType === FundDataType.AnnualizedReturn
            ? (annualizedColumns as any)
            : (priceColumns as any)
        }
        colorTheme="secondary"
        rowLink={({ original: fund }) => ({
          key: `${fund.symbol}`,
          href: `${fundDetailPrefix}[symbol]`,
          as: `${fundDetailPrefix}${fund.symbol.toLowerCase()}`,
        })}
        isClickable
      />
    </FundsTableContainer>
  );
}
